import { Injectable } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import {
    domainValidator,
    phoneLengthValidator,
    repeatingDigitsPhoneValidator,
} from '@common/validators/formValidators';

@Injectable({
    providedIn: 'root',
})
export class FormUtilsService {
    constructor(private formBuilder: FormBuilder) {}

    buildContactForm(): FormGroup {
        return this.formBuilder.group({
            firstName: new FormControl(null, [
                Validators.required,
                Validators.minLength(1),
                Validators.maxLength(39),
                Validators.pattern(/^(?!\s*$)(?=.*[\p{L}])[\p{L}\-' ]*$/u),
            ]),
            lastName: new FormControl(null, [
                Validators.required,
                Validators.minLength(1),
                Validators.maxLength(39),
                Validators.pattern(/^(?!\s*$)(?=.*[\p{L}])[\p{L}\-' ]*$/u),
            ]),
            email: new FormControl(null, [
                Validators.required,
                Validators.minLength(1),
                Validators.maxLength(80),
                domainValidator,
                Validators.pattern(
                    /^[\w]{1,}[\w.+-]{0,}@[\w-]{1,}([.][a-zA-Z]{2,}|[.][\w-]{2,}[.][a-zA-Z]{2,})$/
                ),
            ]),
            phone: new FormControl(null, [
                Validators.required,
                phoneLengthValidator(10, 10),
                repeatingDigitsPhoneValidator(),
            ]),
        });
    }

    trimFormValues(form: FormGroup): void {
        Object.keys(form.controls).forEach((key) => {
            const control = form.get(key);
            if (control && control.value !== null && control.value !== undefined) {
                const trimmedValue = control.value.trim?.();
                if (trimmedValue !== undefined) {
                    if (trimmedValue.length > 0) {
                        control.setValue(trimmedValue);
                    } else {
                        control.setValue(null);
                    }
                }
            }
        });
    }

    sanitizeText(text: string): string {
        if (!text) return '';
        return text.replace(/[<>&]/g, (char) => {
            switch (char) {
                case '<':
                    return '&lt;';
                case '>':
                    return '&gt;';
                case '&':
                    return '&amp;';
                default:
                    return char;
            }
        });
    }

    sanitizeName(name: string | null | undefined): string {
        if (!name) return '';
        return this.sanitizeText(name.replace(/[^\p{L}\-'\s]/gu, ''));
    }

    sanitizeEmail(email: string | null | undefined): string {
        if (!email) return '';
        return this.sanitizeText(email.replace(/[^a-zA-Z0-9.@_+-]/g, ''));
    }

    sanitizePhone(phone: string | null | undefined): string {
        if (!phone) return '';
        return phone.replace(/[^0-9]/g, '');
    }

    sanitizeTextArea(text: string | null | undefined): string {
        if (!text) return '';
        return this.sanitizeText(text);
    }

    handleNameInput(event: Event, form: FormGroup): void {
        const input = event.target as HTMLInputElement;
        const controlName = input.getAttribute('formControlName') || input.id;
        if (input.value) {
            const sanitizedValue = this.sanitizeName(input.value);
            const control = form?.get(controlName);
            if (control && sanitizedValue !== control.value) {
                control.setValue(sanitizedValue, { emitEvent: false });
            }
        }
    }

    handleEmailInput(event: Event, form: FormGroup): void {
        const input = event.target as HTMLInputElement;
        const controlName = input.getAttribute('formControlName') || input.id;
        if (input.value) {
            const sanitizedValue = this.sanitizeEmail(input.value);
            const control = form?.get(controlName);
            if (control && sanitizedValue !== control.value) {
                control.setValue(sanitizedValue, { emitEvent: false });
            }
        }
    }

    sanitizeContactFormValues(formValues: any): any {
        if (!formValues) return {};
        return {
            firstName: this.sanitizeName(formValues.firstName),
            lastName: this.sanitizeName(formValues.lastName),
            email: this.sanitizeEmail(formValues.email),
            phone: this.sanitizePhone(formValues.phone),
            ...Object.keys(formValues)
                .filter((key) => !['firstName', 'lastName', 'email', 'phone'].includes(key))
                .reduce((obj, key) => ({ ...obj, [key]: formValues[key] }), {}),
        };
    }

    sanitizeProfileData(profile: any): any {
        if (!profile) return {};

        const sanitizedProfile = { ...profile };

        if (sanitizedProfile.firstName)
            sanitizedProfile.firstName = this.sanitizeName(sanitizedProfile.firstName);
        if (sanitizedProfile.lastName)
            sanitizedProfile.lastName = this.sanitizeName(sanitizedProfile.lastName);
        if (sanitizedProfile.email)
            sanitizedProfile.email = this.sanitizeEmail(sanitizedProfile.email);
        if (sanitizedProfile.phone)
            sanitizedProfile.phone = this.sanitizePhone(sanitizedProfile.phone);
        if (sanitizedProfile.concerns)
            sanitizedProfile.concerns = this.sanitizeTextArea(sanitizedProfile.concerns);

        return sanitizedProfile;
    }
}
